import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebaseConfig";

const calls = Object.fromEntries(["getBillingAccount", "getBillingInvoice", "getBillingInvoiceDraft", "manageBillingAccount", "createPaystackCheckout", "verifyPaystackPayment", "getAutomaticBilling", "configureAutomaticBilling", "checkAutomaticBilling"]
  .map((name) => [name, httpsCallable(functions, name, { timeout: 95000 })]));
const inFlight = new Map();
const pending = new Map();
function user() { const uid = auth.currentUser?.uid; if (!uid) throw new Error("Sign in again to open billing."); return uid; }
async function read(name, data) {
  const uid = user(), key = uid + "|" + name + "|" + JSON.stringify(data);
  if (inFlight.has(key)) return inFlight.get(key);
  const promise = calls[name](data).then((result) => {
    if (auth.currentUser?.uid !== uid) throw new Error("Sign in again to open billing.");
    return result.data;
  }).finally(() => inFlight.delete(key));
  inFlight.set(key, promise); return promise;
}
export const getBillingAccount = (orgId) => read("getBillingAccount", { orgId });
export const getBillingInvoice = (orgId, invoiceId) => read("getBillingInvoice", { orgId, invoiceId });
export const getBillingInvoiceDraft = (orgId, periodKey) => read("getBillingInvoiceDraft", { orgId, periodKey });
export const verifyInvoicePayment = (orgId, reference) => read("verifyPaystackPayment", { orgId, reference });
export const getAutomaticBilling = (orgId) => read("getAutomaticBilling", { orgId });
export const checkAutomaticBilling = (orgId) => read("checkAutomaticBilling", { orgId });
function keyFor(data) {
  const raw = JSON.stringify(data);
  let a = 2166136261, b = 2246822519;
  for (let i = 0; i < raw.length; i += 1) { a = Math.imul(a ^ raw.charCodeAt(i), 16777619); b = Math.imul(b ^ raw.charCodeAt(i), 3266489917); }
  return "matrixos-billing-request-" + user() + "-" + data.orgId + "-" + (a >>> 0).toString(16) + (b >>> 0).toString(16);
}
async function mutate(name, data, fresh = false) {
  const uid = user(), key = keyFor({ name, ...data });
  let requestId = fresh ? null : pending.get(key);
  try { if (!fresh && !requestId) requestId = window.localStorage.getItem(key); } catch { /* The in-memory token remains available. */ }
  if (!requestId) requestId = window.crypto?.randomUUID?.() || "req-" + Date.now() + "-" + Math.random().toString(36).slice(2);
  pending.set(key, requestId);
  try { window.localStorage.setItem(key, requestId); } catch { /* Backend receipt and bank/provider keys still deduplicate. */ }
  const result = await calls[name]({ ...data, requestId });
  if (auth.currentUser?.uid !== uid) throw new Error("Sign in again to check the billing action.");
  if (!result.data || (name === "manageBillingAccount" && result.data.ok !== true)) throw new Error("The billing action was not confirmed. Refresh before trying again.");
  pending.delete(key);
  try { window.localStorage.removeItem(key); } catch { /* Safe repeated request returns its original receipt. */ }
  return result.data;
}
export const manageBillingAccount = (orgId, data) => mutate("manageBillingAccount", { orgId, ...data });
export const configureAutomaticBilling = (orgId, data) => mutate("configureAutomaticBilling", { orgId, ...data });
export const runAutomaticBilling = (orgId, data) => mutate("checkAutomaticBilling", { orgId, ...data, issue: true });
export const startInvoiceCheckout = (orgId, selection, fresh = false) => mutate("createPaystackCheckout", { orgId, selection, callbackUrl: window.location.href }, fresh);
export function inputCents(value, signed = false) {
  const raw = String(value ?? "").trim();
  const match = raw.match(signed ? /^(-?)(\d+)(?:[.,](\d{1,2}))?$/ : /^()(\d+)(?:[.,](\d{1,2}))?$/);
  if (!match) throw new Error("Enter an amount with no more than two decimal places.");
  const amount = Number(match[2]) * 100 + Number((match[3] || "").padEnd(2, "0"));
  const result = match[1] === "-" ? -amount : amount;
  if (!Number.isSafeInteger(result) || Math.abs(result) > 1000000000) throw new Error("Enter a valid amount.");
  return result;
}
export function billingError(error) {
  const code = String(error?.code || "").replace(/^functions\//, "");
  if (code === "permission-denied") return "Your account does not have permission for this billing action.";
  if (code === "unauthenticated") return "Sign in again to open billing.";
  if (code === "internal" || code === "deadline-exceeded") return "The billing action has not been confirmed. Refresh and check the balance before retrying.";
  return error?.message || "Billing could not be loaded. Please try again.";
}
